<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>
		<breadcrumbs :breadcrumbs="page.breadcrumbs" :class="page.headingImage ? '' : 'no-slider'" />

		<section class="main-content first">
			<div class="row">
				<div class="columns column12">
					<h1>{{ page.title || page.header }}</h1>
					<strong v-if="page.date" class="date">
						{{ $d(new Date(page.date.date.split(' ')[0]), 'short') }}
					</strong>
					<div v-parse-links v-html="page.introContent" />
					<picture>
						<source v-if="page.sideImageWebp" :srcset="page.sideImageWebp" type="image/webp" />
						<source :srcset="page.sideImage" />
						<img :src="page.sideImage" class="extra-image" loading="lazy" :alt="page.sideImageAlt" />
					</picture>
					<div v-parse-links v-html="page.content" />
				</div>
			</div>
		</section>

		<frequently-asked-questions
			v-if="page && page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>

		<section v-if="prev || next">
			<div class="row other-items">
				<nuxt-link v-if="prev" :to="prevFilename" class="columns column6 left">
					<font-awesome-icon icon="fa-light fa-chevron-circle-left" size="1x" />
					<div>
						<h3>{{ prev.title || prev.header }}</h3>
						<div>
							<div v-parse-links v-html="prev.overviewContent" />
						</div>
					</div>
					<picture>
						<source v-if="prev.overviewImageWebp" :srcset="prev.overviewImageWebp" type="image/webp" />
						<source :srcset="prev.overviewImage" />
						<img loading="lazy" :src="prev.overviewImage" :alt="prev.overviewImageAlt" />
					</picture>
				</nuxt-link>
				<nuxt-link v-if="next" :to="nextFilename" class="columns column6 right">
					<font-awesome-icon icon="fa-light fa-chevron-circle-right" size="1x" />

					<div>
						<h3>{{ next.title || next.header }}</h3>
						<div>
							<div v-parse-links v-html="next.overviewContent" />
						</div>
					</div>
					<picture>
						<source v-if="next.overviewImageWebp" :srcset="next.overviewImageWebp" type="image/webp" />
						<source :srcset="next.overviewImage" />
						<img loading="lazy" :src="next.overviewImage" :alt="next.overviewImageAlt" />
					</picture>
				</nuxt-link>
			</div>
		</section>

		<contentblocks
			v-if="page && page.contentblocks && page.contentblocks.length"
			:contentblocks="page.contentblocks"
			:content-blocks-content="page.sectionContentblocksContent"
			:content-blocks-subtitle="page.sectionContentblocksTitle"
		/>
		<pre-footer
			v-if="page && page.footerSection && page.footerSection.length"
			:footer-section="page.footerSection[0]"
		/>
		<newsletter
			v-if="page && page.showNewsletter"
			:header="defaults[locale].newsletter.title"
			:content="defaults[locale].newsletter.content"
			:button="defaults[locale].newsletter.button"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { locale } = useI18n();

await fetchPage();

const { data: news } = await useWebsiteFetch('news', {
	query: { language: locale.value },
	key: `${locale.value}/news`,
});

const idx = news.value.findIndex((newsPage) => newsPage.pageID === page.value.pageID);
const next = idx >= 0 && idx < news.value.length - 1 ? news.value[idx + 1] : null;
const prev = idx > 0 ? news.value[idx - 1] : null;

// Using localePath in the template triggers bug https://github.com/vuejs/core/issues/5657, this is a workaround
const app = useNuxtApp();
const nextFilename = next && app.$localePath(`/${next.filename}`);
const prevFilename = prev && app.$localePath(`/${prev.filename}`);
</script>

<style lang="scss" scoped>
.intro {
	padding: 50px 0;
}

.breadcrumbs ul {
	margin: 0 0 40px;
}

.extra-image {
	width: 100%;
	max-width: 600px;
	margin: 20px 0;
}

.date {
	margin: 0 0 20px;
	font-weight: 700;
	width: 100%;
	display: inline-block;
}

.hero-image img {
	max-width: 1100px;
	margin: 180px auto -100px;
}

.news-item .intro h3 {
	margin: 20px 0;
}

.main-content .row {
	max-width: 900px;
}

.other-items {
	margin: 25px auto 50px;
	justify-content: center;
}

.other-items,
.other-items a {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	align-items: center;
}

.other-items a {
	flex-flow: row-reverse nowrap;
	border-right: 2px solid #5c936d;
	gap: 20px;
	text-align: right;
}

.other-items span {
	color: $body-color;
}

.other-items a:nth-child(odd) {
	flex-direction: row;
	border-right: none;
	border-left: 2px solid #5c936d;
	text-align: left;
}

.other-items h3 {
	font-size: 22px;
}

.other-items img {
	width: 160px;
}

.other-items i,
.other-items svg {
	font-size: 50px;
	margin: 0 10px;
}

blockquote {
	font-size: 22px;
	padding: 0 0 0 20px;
	border-left: 5px solid #ccc;
	width: 90%;
	margin: 35px 0 35px 10%;
}

@media (max-width: 920px) {
	.other-items a {
		border: none !important;
		margin: 20px auto;
	}

	.other-items i,
	.other-items svg {
		font-size: 40px;
		margin: 0 3px;
	}
}

@media (max-width: 680px) {
	.other-items {
		img {
			max-width: 80px;
		}

		a {
			gap: 5px;
		}
	}
}

@media (max-width: 460px) {
	.other-items img {
		display: none;
	}
}
</style>
